import { useCallback, useContext, useEffect } from 'react';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { LoaderContext } from '@contexts/loader/loader-context';
import {
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { queryClient } from '../query-client';

import { useGetTokenQueryKey } from './auth/type';
import { useRequireAuthTokens } from './auth/use-get-tokens';
import { useTokenManagement } from './auth/use-token-management';
import { useToast } from './use-toast/use-toast';

export const useGenericInfiniteQuery = <TQueryFnData = unknown, TQueryKey extends QueryKey = QueryKey>(
  key: TQueryKey,
  fn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<UseInfiniteQueryOptions<TQueryFnData, unknown, TQueryFnData, TQueryKey>, 'queryFn' | 'queryKey'>,
  generalLoading?: boolean
): UseInfiniteQueryResult<TQueryFnData, unknown> => {
  const { incrementLoaderQueue, decrementLoaderQueue } = useContext(LoaderContext);
  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const toast = useToast();
  const requestId = `infinite-query-${key}`;

  const { getTokens } = useTokenManagement();

  const { accessToken, refreshToken } = getTokens();

  const { mutate: requireAuthTokens } = useRequireAuthTokens({
    refreshToken: refreshToken ?? '',
  });

  const handleAccesToken = useCallback(() => {
    requireAuthTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    const isMutatingRefreshTokenQuantity = queryClient.isMutating({ mutationKey: [useGetTokenQueryKey], exact: true });
    if (!accessToken && refreshToken && isMutatingRefreshTokenQuantity === 0) {
      handleAccesToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, refreshToken]);

  const query = useInfiniteQuery(key, fn, {
    enabled: (!!accessToken || isPublicEventReportPage) && options?.enabled,
    onSuccess: (data) => {
      if (options?.onSuccess) options.onSuccess(data);
      decrementLoaderQueue(requestId);
    },
    onError: (error) => {
      if (options?.onError) options.onError(error);
      if (error instanceof RequestError) {
        if (showToastErrorRequest(error)) {
          toast.push({
            title: error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
      decrementLoaderQueue(requestId);
    },
    onSettled: (data, error) => {
      if (options?.onSettled) options.onSettled(data, error);
      decrementLoaderQueue(requestId);
    },
    getNextPageParam: options?.getNextPageParam,
    getPreviousPageParam: options?.getPreviousPageParam,
    keepPreviousData: options?.keepPreviousData,
  });

  useEffect(() => {
    if (query.isInitialLoading && query.isRefetching) {
      incrementLoaderQueue(requestId);
    }
    return () => {
      if (query.isInitialLoading) {
        decrementLoaderQueue(requestId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isInitialLoading, query.isRefetching, generalLoading]);

  return query;
};
